import type {
  CreateTenantPayload,
  ITenant,
  ITenantLanguage,
  UpdateTenantPayload,
} from '@/shared/api/types/tenant-types'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import {
  useCreateTenantMutation,
  useLanguagesQuery,
  useTenantLanguages,
  useUpdateTenantInfoMutation,
  useUpdateTenantMutation,
} from '@/entities/tenant'
import { isReservedTenantSlug, TENANT_SLUG_PATTERN } from '@/shared/libs/admin-routes'
import { getApiErrorText } from '@/shared/libs/get-api-error-text'
import { optionalPhoneSchema } from '@/shared/libs/phone-validation'
import { showToast } from '@/shared/libs/toast'
import { Button } from '@/shared/ui/button'
import { FormField } from '@/shared/ui/form-field'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/shared/ui/sheet'
import { Switch } from '@/shared/ui/switch'
import { Textarea } from '@/shared/ui/textarea'

const basicInfoSchema = z.object({
  name: z.string().trim().min(1, 'common.required').max(100, 'tenant.validation.nameMaxLength'),
  slug: z
    .string()
    .trim()
    .min(1, 'common.required')
    .regex(TENANT_SLUG_PATTERN, 'tenant.validation.slugInvalid')
    .refine((value) => !isReservedTenantSlug(value), 'tenant.validation.slugReserved'),
  description: z.string().optional(),
  contactEmail: z.string().trim().min(1, 'common.required').email('common.invalidEmail'),
  phone: optionalPhoneSchema,
  website: z.string().trim().url('common.invalidUrl').or(z.literal('')).optional(),
  defaultLanguage: z.string().min(1, 'common.required'),
  isActive: z.boolean(),
})

type BasicInfoValues = z.infer<typeof basicInfoSchema>

interface LocalizedValue {
  name: string
  description: string
}

interface TenantFormDrawerProps {
  open: boolean
  tenant?: ITenant | null
  onClose: () => void
  onSuccess: () => void
}

function normalizeSlugInput(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
}

function buildLocalizedData(
  languages: ITenantLanguage[],
  values: Record<string, LocalizedValue>,
): Record<string, Record<string, string>> {
  return Object.fromEntries(
    languages
      .filter((lang) => lang.isActive)
      .map((lang) => [
        lang.languageCode,
        {
          name: values[lang.languageCode]?.name ?? '',
          description: values[lang.languageCode]?.description ?? '',
        },
      ]),
  )
}

export function TenantFormDrawer({ open, tenant, onClose, onSuccess }: TenantFormDrawerProps) {
  const { t } = useTranslation()
  const isEditMode = Boolean(tenant)

  const createMutation = useCreateTenantMutation()
  const updateMutation = useUpdateTenantMutation()
  const updateInfoMutation = useUpdateTenantInfoMutation()
  const { data: languagesResponse, isLoading: isLanguagesLoading } = useLanguagesQuery(open)
  const tenantLanguagesQuery = useTenantLanguages(tenant?.id ?? '')

  const isBasicPending = createMutation.isPending || updateMutation.isPending
  const isInfoPending = updateInfoMutation.isPending

  const activeLanguages = (tenantLanguagesQuery.items ?? []).filter((l) => l.isActive)

  const languageOptions = (languagesResponse?.data ?? [])
    .filter((language) => language.isActive)
    .map((language) => ({
      label: language.nativeName || language.name || language.code,
      value: language.id,
    }))

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    control,
    formState: { errors },
  } = useForm<BasicInfoValues>({
    resolver: zodResolver(basicInfoSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      slug: '',
      description: '',
      contactEmail: '',
      phone: '',
      website: '',
      defaultLanguage: '',
      isActive: true,
    },
  })

  const defaultLanguage = useWatch({ control, name: 'defaultLanguage' })
  const isActive = useWatch({ control, name: 'isActive' })

  const [localizedValues, setLocalizedValues] = useState<Record<string, LocalizedValue>>({})

  useEffect(() => {
    if (!open) return
    if (tenant) {
      reset({
        name: tenant.name,
        slug: tenant.slug,
        description: tenant.description,
        contactEmail: tenant.contactEmail,
        website: tenant.website ?? '',
        phone: tenant.phone ?? '',
        defaultLanguage: tenant.defaultLanguage?.id ?? '',
        isActive: tenant.isActive,
      })

      const initialLocalized: Record<string, LocalizedValue> = {}
      if (tenant.localizedData) {
        for (const [code, values] of Object.entries(tenant.localizedData)) {
          initialLocalized[code] = {
            name: values['name'] ?? '',
            description: values['description'] ?? '',
          }
        }
      }
      // eslint-disable-next-line react-hooks/set-state-in-effect -- initializes local state from external tenant data when the drawer opens
      setLocalizedValues(initialLocalized)
    } else {
      reset({
        name: '',
        slug: '',
        description: '',
        contactEmail: '',
        phone: '',
        website: '',
        defaultLanguage: '',
        isActive: true,
      })
      setLocalizedValues({})
    }
  }, [open, tenant, reset])

  function handleSlugChange(e: React.ChangeEvent<HTMLInputElement>) {
    setValue('slug', normalizeSlugInput(e.target.value), {
      shouldDirty: true,
      shouldValidate: true,
    })
  }

  async function onSubmitBasicInfo(values: BasicInfoValues) {
    try {
      if (isEditMode && tenant) {
        const payload: UpdateTenantPayload = {
          name: values.name,
          slug: values.slug,
          description: values.description ?? '',
          website: values.website ?? '',
          phone: values.phone ?? '',
          isActive: values.isActive,
          ...(values.contactEmail !== undefined ? { contactEmail: values.contactEmail } : {}),
        }
        await updateMutation.mutateAsync({ id: tenant.id, payload })
        showToast.success(t('tenant.updateSuccess'))
        onSuccess()
      } else {
        const payload: CreateTenantPayload = {
          name: values.name,
          slug: values.slug,
          description: values.description ?? '',
          contactEmail: values.contactEmail ?? '',
          website: values.website ?? '',
          isActive: true,
          logoUrl: '',
          ...(values.phone ? { phone: values.phone } : {}),
          defaultLanguage: values.defaultLanguage ?? '',
        }
        await createMutation.mutateAsync(payload)
        showToast.success(t('tenant.createSuccess'))
        onClose()
        onSuccess()
      }
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    }
  }

  async function onSubmitLocalizedInfo() {
    if (!tenant) return
    try {
      const localizedData = buildLocalizedData(activeLanguages, localizedValues)
      await updateInfoMutation.mutateAsync({ id: tenant.id, payload: { localizedData } })
      showToast.success(t('tenant.updateSuccess'))
      onSuccess()
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    }
  }

  const drawerTitle = isEditMode ? t('tenant.edit') : t('tenant.create')
  const getErrorText = (message: string | undefined) =>
    message ? t(message, { defaultValue: message }) : undefined

  return (
    <Sheet
      open={open}
      onOpenChange={(open_) => {
        if (!open_) onClose()
      }}
    >
      <SheetContent className="w-[520px] sm:max-w-[520px] overflow-y-auto flex flex-col">
        <SheetHeader>
          <SheetTitle>{drawerTitle}</SheetTitle>
        </SheetHeader>

        <div className="flex-1 space-y-6 py-4">
          <p className="text-xs text-zinc-500">
            <span className="text-red-500">*</span>{' '}
            {t('tenant.requiredHint', {
              defaultValue: 'là trường bắt buộc; các trường còn lại là tuỳ chọn.',
            })}
          </p>

          {/* Section 1: Basic Info */}
          <form
            id="tenant-basic-form"
            onSubmit={handleSubmit(onSubmitBasicInfo)}
            autoComplete="off"
            className="space-y-4"
          >
            <FormField label={t('tenant.name')} required error={getErrorText(errors.name?.message)}>
              <Input autoComplete="off" {...register('name')} />
            </FormField>

            <FormField label={t('tenant.slug')} required error={getErrorText(errors.slug?.message)}>
              <Input autoComplete="off" {...register('slug', { onChange: handleSlugChange })} />
            </FormField>

            <FormField
              label={t('common.description')}
              optional
              optionalLabel={t('common.optional')}
              error={getErrorText(errors.description?.message)}
            >
              <Textarea rows={3} {...register('description')} />
            </FormField>

            <FormField
              label={t('tenant.contactEmail')}
              required
              error={getErrorText(errors.contactEmail?.message)}
            >
              <Input inputMode="email" autoComplete="email" {...register('contactEmail')} />
            </FormField>

            <FormField
              label={t('tenant.phone')}
              optional
              optionalLabel={t('common.optional')}
              error={getErrorText(errors.phone?.message)}
            >
              <Input type="tel" {...register('phone')} />
            </FormField>

            <FormField
              label={t('tenant.website')}
              optional
              optionalLabel={t('common.optional')}
              error={getErrorText(errors.website?.message)}
            >
              <Input {...register('website')} />
            </FormField>

            <FormField
              label={t('tenant.defaultLanguage')}
              required
              error={getErrorText(errors.defaultLanguage?.message)}
            >
              <Select
                value={defaultLanguage ?? ''}
                onValueChange={(v) =>
                  setValue('defaultLanguage', v, { shouldDirty: true, shouldValidate: true })
                }
                disabled={isLanguagesLoading || isBasicPending}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('tenant.defaultLanguage')} />
                </SelectTrigger>
                <SelectContent>
                  {languageOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>

            {isEditMode && (
              <div className="flex items-center gap-2">
                <Switch checked={isActive} onCheckedChange={(v) => setValue('isActive', v)} />
                <Label>{t('common.status')}</Label>
              </div>
            )}

            <div className="flex justify-end">
              <Button type="submit" disabled={isBasicPending} aria-busy={isBasicPending}>
                {isBasicPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isBasicPending
                  ? isEditMode
                    ? t('common.saving')
                    : t('common.creating', { defaultValue: 'Đang tạo...' })
                  : isEditMode
                    ? t('common.update')
                    : t('common.create')}
              </Button>
            </div>
          </form>

          {/* Section 2: Localized Data (edit mode only) */}
          {isEditMode && activeLanguages.length > 0 && (
            <div className="space-y-4 border-t border-zinc-100 pt-6">
              <p className="text-sm font-medium text-zinc-800">
                {t('tenant.localizedInfo', { defaultValue: 'Thông tin đa ngôn ngữ' })}
              </p>

              {activeLanguages.map((lang) => (
                <div
                  key={lang.languageCode}
                  className="space-y-3 rounded-lg border border-zinc-200 p-3"
                >
                  <p className="text-xs font-semibold uppercase text-zinc-500">
                    {lang.languageName} ({lang.languageCode})
                    {tenant?.defaultLanguage?.code === lang.languageCode && (
                      <span className="ml-2 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium normal-case text-primary">
                        {t('tenant.language.default')}
                      </span>
                    )}
                  </p>

                  <FormField label={t('tenant.name')}>
                    <Input
                      value={localizedValues[lang.languageCode]?.name ?? ''}
                      onChange={(e) =>
                        setLocalizedValues((prev) => ({
                          ...prev,
                          [lang.languageCode]: {
                            name: e.target.value,
                            description: prev[lang.languageCode]?.description ?? '',
                          },
                        }))
                      }
                    />
                  </FormField>

                  <FormField
                    label={t('common.description')}
                    optional
                    optionalLabel={t('common.optional')}
                  >
                    <Textarea
                      rows={2}
                      value={localizedValues[lang.languageCode]?.description ?? ''}
                      onChange={(e) =>
                        setLocalizedValues((prev) => ({
                          ...prev,
                          [lang.languageCode]: {
                            name: prev[lang.languageCode]?.name ?? '',
                            description: e.target.value,
                          },
                        }))
                      }
                    />
                  </FormField>
                </div>
              ))}

              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="secondary"
                  disabled={isInfoPending}
                  aria-busy={isInfoPending}
                  onClick={onSubmitLocalizedInfo}
                >
                  {isInfoPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isInfoPending
                    ? t('common.saving')
                    : t('tenant.updateLocalized', { defaultValue: 'Cập nhật đa ngôn ngữ' })}
                </Button>
              </div>
            </div>
          )}
        </div>

        <SheetFooter className="pt-4 border-t border-zinc-100">
          <Button variant="outline" onClick={onClose} type="button">
            {t('common.close', { defaultValue: 'Đóng' })}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
